import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { countryCodeFromPath } from '../mapUtilities'
import BoundariesList from './MapOptions/Bounds/BoundariesList'
import LabelsList from './MapOptions/Labels/LabelsList'
import LayersList from './MapOptions/Layers/LayersList'
import BaseLayersList from './BaseLayersList'
import ToggleSwitch from '../../Utilities/ToggleSwitch'
import { handleModalVisibility, toggleModal } from '../../../common/util'
import { setLegendModalOpen } from '../../../ducks/mapControls'
import {fetchPlaceSources,} from '../../../ducks/sources'


/**
 * Renders the MapLayersContent component.
 *
 * @component
 * @param {Function} props.handleClose - The function to handle closing the component and relevant modals.
 * @returns {JSX.Element} The rendered MapLayersContent component.
 */
const MapLayersContent = ({
  handleClose,
  showStyleLegend,
  setShowStyleLegend
  }) => {
  const dispatch = useDispatch()
  const workingBoundary = useSelector((state) => state.boundaries.workingBoundary)
  // Country code determines available layers content
  const workingCountryCode = workingBoundary ? countryCodeFromPath(workingBoundary) : null
  const isWorkingInUSBoundary = (workingCountryCode === 'us') // boolean
  const [isOpen, setIsOpen] = useState({
    sectionLabels:     true,
    sectionSettings:   false, // default to closed
    sectionBoundaries: false, // default to closed
    sectionLayers:     true,
    sectionBasemaps:   true,
  })
  const isWideScreen = window.innerWidth > 1165
  const isWideWaterfall = Boolean(document.querySelector('#mapbox.with-wide-waterfall'))
  const allowMultipleModals = isWideScreen && !isWideWaterfall
  const isLegendModalOpen = useSelector(state => state.mapControls.legendModalOpen)
  const placePath = window.location.pathname.replace(/\/+$/, '')

    // Fetch when placePath changes
    useEffect(() => {
      if(placePath) dispatch(fetchPlaceSources(placePath))
    }, [placePath, dispatch])

  const toggleSection = (e, section) => {
    setIsOpen((prevState) => ({ ...prevState, [section]: !prevState[section] }))
    // toggle the angle icon animation
    e.currentTarget.lastChild.classList.toggle('rotate-180')
  }
  const toggleLegendModal = () => {
    toggleModal({
      dispatch,
      isOpen:        isLegendModalOpen,
      setOpenAction: setLegendModalOpen,
      allowMultipleModals,
      handleModalVisibility,
      modalKey:      'layersModalOpen',
      keepAlso:      ['layersModalOpen'],
    })
  }
  const handleToggle = e => {
    const next = e.target.checked
    setShowStyleLegend(next)
    toggleLegendModal()
  }

  const handleExpandVertical = () => {
    // Parallels click listener in _show_mapbox_gl.html.haml
    document.getElementById('waterfall').classList.toggle('expanded-vertical')
    document.getElementById('mapbox').classList.toggle('expanded-vertical')
  }

  return (
    <div id ='map-layers-pane' className="map-layers-modal rounded-lg shadow-gl padded">
      <div className="map-options-title-row padding-top-lg">
        <h3 className="map-options-title">Layers</h3>
        <div className="expand-vertical flex-row-center nomobile" onClick={handleExpandVertical}>
          <i className="fas fa-arrows-up-down"></i>
        </div>
        <div className="close" onClick={handleClose}>
          <i className="fa fa-times"></i>
        </div>
      </div>
      <div className="map-options-content">
        {/* View Settings */}
        <div
          className={`map-options-content-header${
            !isOpen.sectionSettings ? ' collapsed-header' : ''
          }`}
          onClick={(e) => toggleSection(e, 'sectionSettings')}
        >
          <h4 className="">Map Elements</h4>
          <i className="fa fa-angle-down h4 rotate-0"></i>
        </div>
        <div
          className={`map-options-content-body collapse ${
            isOpen.sectionSettings ? 'in' : ''
          }`}
        >

          <div
            className={'toggle-form-group'}>
            <label htmlFor="show-legend" className="form-label">
              Show Legend on Map
              </label>

            <ToggleSwitch
              id="show-legend"
              size="medium"
              checked={showStyleLegend}
              onChange={handleToggle}
              />

          </div>
        <BoundariesList
            parent="map-layers"
            isWorkingInUSBoundary={isWorkingInUSBoundary}
          />
        </div>
        <div
          className={`map-options-content-header${
            !isOpen.sectionBasemaps ? ' collapsed-header' : ''
          }`}
          onClick={(e) => toggleSection(e, 'sectionBasemaps')}
        >
          <h4 className="">Basemaps</h4>
          <i className="fa fa-angle-up h4 rotate-0"></i>
        </div>
        <div
          className={`map-options-content-body collapse ${
            isOpen.sectionBasemaps ? 'in' : ''
          }`}
        >
          <BaseLayersList handleClose={handleClose} />
        </div>
        <div
          className={`map-options-content-header${
            !isOpen.sectionLabels ? ' collapsed-header' : ''
          }`}
          onClick={(e) => toggleSection(e, 'sectionLabels')}
        >
          <h4 className="">Labels</h4>
          <i className="fa fa-angle-up h4 rotate-0"></i>
        </div>
        <div
          className={`map-options-content-body collapse ${
            isOpen.sectionLabels ? 'in' : ''
          }`}
        >
          <LabelsList
            isWorkingInUSBoundary={isWorkingInUSBoundary}
            workingCountryCode={workingCountryCode}
          />
        </div>
        <div
          className={`map-options-content-header${
            !isOpen.sectionLayers ? ' collapsed-header' : ''
          }`}
          onClick={(e) => toggleSection(e, 'sectionLayers')}
        >
          <h4 className="">Additional Layers</h4>
          <i className="fa fa-angle-up h4 rotate-0"></i>
        </div>
        <div
          className={`map-options-content-body collapse ${
            isOpen.sectionLayers ? 'in' : ''
          }`}
        >
          <LayersList
            isWorkingInUSBoundary={isWorkingInUSBoundary}
            workingCountryCode={workingCountryCode}
          />
        </div>
      </div>
    </div>
  )
}

MapLayersContent.propTypes = {
  handleClose:        PropTypes.func.isRequired,
  setShowStyleLegend: PropTypes.func,
  showStyleLegend:    PropTypes.bool
}

export default MapLayersContent